import React, { useCallback, useState } from 'react'
import { Modal } from '@musinsa/mwms-ds'
import { PrimaryButton, SecondaryButton, Typography } from '@musinsa/layer-components-desktop'
import styled from 'styled-components'
import { Icon } from '@musinsa/pbo-assets'
import { format, max, parseISO, startOfToday } from 'date-fns'
import { AxiosError } from 'axios'
import { DatePickerValue } from '@/components/content/DatePicker/DatePickerInput'
import { PeriodField } from '@/components/content/PeriodField'
import { useCreateBundleCondition, useCreateBundleConditionSku } from '@/services/outbound/singleBatchSetting/useSingleBatchSettingQueries'
import { SingleBatchSettingItem } from '@/services/outbound/singleBatchSetting/types'
import { formatTimeInput, isValidTime } from '@/pages/outbound/singleBatchSetting/utils/timeUtils'
import MSnackBar from '@/utils/modal/MSnackBar'

interface Props {
  row: SingleBatchSettingItem
  onClose: () => void
  onSuccess: () => void
  onDirectFailure: (message: string) => void
}

function SingleBatchSettingPeriodEditModal({ row, onClose, onSuccess, onDirectFailure }: Props) {
  const [startDate, setStartDate] = useState<DatePickerValue>(parseISO(row.startAt))
  const [startTime, setStartTime] = useState(format(parseISO(row.startAt), 'HH:mm'))
  const [endDate, setEndDate] = useState<DatePickerValue>(parseISO(row.endAt))
  const [endTime, setEndTime] = useState(format(parseISO(row.endAt), 'HH:mm'))

  const { mutateAsync: createBundleCondition, isPending: isCreating } = useCreateBundleCondition()
  const { mutateAsync: createBundleConditionSku, isPending: isCreatingSku } = useCreateBundleConditionSku()
  const isPending = isCreating || isCreatingSku

  const currentStartAt = startDate && isValidTime(startTime) ? `${format(startDate as Date, 'yyyy-MM-dd')}T${startTime}:00` : ''
  const currentEndAt = endDate && isValidTime(endTime) ? `${format(endDate as Date, 'yyyy-MM-dd')}T${endTime}:00` : ''

  const normalizeAt = (iso: string) => format(parseISO(iso), "yyyy-MM-dd'T'HH:mm") + ':00'
  const isUnchanged = currentStartAt === normalizeAt(row.startAt) && currentEndAt === normalizeAt(row.endAt)
  const isEndBeforeStart = !!currentEndAt && currentEndAt <= currentStartAt
  const isEndBeforeNow = !!currentEndAt && currentEndAt <= format(new Date(), "yyyy-MM-dd'T'HH:mm") + ':00'
  const endMinDate = startDate ? max([startOfToday(), startDate as Date]) : startOfToday()
  const isSubmitDisabled = isPending || !currentStartAt || !currentEndAt || isUnchanged || isEndBeforeStart || isEndBeforeNow

  const handleSubmit = useCallback(async () => {
    if (!startDate || !endDate) return
    try {
      if (row.conditionType === 'CENTER_SKU') {
        // sku 행이면 barcodes 에 skuId 미리보기(최대 20건)가 담긴다 — 서버 응답 필드명을 그대로 쓴다.
        // 서버 updateSku 가 이미 있는 skuId 를 빼고 넣으므로 명단은 그대로고 기간만 바뀐다
        await createBundleConditionSku({
          warehouseCode: row.warehouseCode ?? '',
          startAt: currentStartAt,
          endAt: currentEndAt,
          skuIds: row.barcodes,
        })
      } else {
        await createBundleCondition({
          warehouseCode: row.warehouseCode,
          conditionType: row.conditionType,
          startAt: currentStartAt,
          endAt: currentEndAt,
          barcodes: row.conditionType === 'CENTER_BARCODE' ? row.barcodes : undefined,
        })
      }
      MSnackBar.show('단수합포 기간이 수정되었습니다', 'success')
      onSuccess()
    } catch (e) {
      const err = e as AxiosError<ApiResponseV2>
      const message = err.response?.data?.meta?.message || '단수합포 기간 수정에 실패했습니다.'
      onDirectFailure(message)
    }
  }, [startDate, endDate, currentStartAt, currentEndAt, row, createBundleCondition, createBundleConditionSku, onSuccess, onDirectFailure])

  return (
    <Modal open customWidth={640} hideFooter title="단수합포 수정" onClose={onClose}>
      <BodyContent>
        <InfoBanner>
          <Icon name="ic-infoCircle" size={20} filled color="#8a8a8a" />
          <Typography name="base-text-l-medium" colorToken="fg">
            등록된 상품의 단수합포 기간이 일괄 변경됩니다.
          </Typography>
        </InfoBanner>

        <FieldGroup>
          <FieldLabelRow>
            <Typography name="base-text-l-medium" colorToken="fg">
              기간
            </Typography>
            <Typography name="base-text-l-medium" colorToken="layerDesktop-fg-accent">
              (필수)
            </Typography>
          </FieldLabelRow>
          <PeriodRow>
            <PeriodField
              label="시작일시"
              date={startDate}
              time={startTime}
              onDateChange={setStartDate}
              onTimeChange={(e) => setStartTime(formatTimeInput(e.target.value))}
              datePlaceholder="시작일"
              timePlaceholder="시작시간"
              maxDate={(endDate as Date) ?? undefined}
              errorText={isEndBeforeStart ? '시작일시는 종료일시 이전으로 설정하세요.' : undefined}
            />
            <PeriodField
              label="종료일시"
              date={endDate}
              time={endTime}
              onDateChange={setEndDate}
              onTimeChange={(e) => setEndTime(formatTimeInput(e.target.value))}
              datePlaceholder="종료일"
              timePlaceholder="종료시간"
              minDate={endMinDate}
              errorText={isEndBeforeStart ? '종료일시는 시작일시보다 늦어야 합니다.' : isEndBeforeNow ? '종료일시는 현재 시간보다 늦어야 합니다.' : undefined}
            />
          </PeriodRow>
        </FieldGroup>
      </BodyContent>

      <ModalFooter>
        <SecondaryButton variant="outlined" size="large" widthMode="compact" label="취소" onClick={onClose} />
        <PrimaryButton variant="filled" size="large" widthMode="compact" label="변경" disabled={isSubmitDisabled} onClick={handleSubmit} />
      </ModalFooter>
    </Modal>
  )
}

export default SingleBatchSettingPeriodEditModal

const BodyContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const InfoBanner = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: var(--banner-info-bg, rgba(224, 224, 224, 0.2));
  border: 1px solid var(--banner-info-border, #ebebeb);
  border-radius: 4px;
  padding: 16px 12px;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabelRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const PeriodRow = styled.div`
  display: flex;
  gap: 8px;
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid var(--stroke-divider-low, rgba(138, 138, 138, 0.1));
  margin-top: 20px;
  padding-top: 16px;
`
